import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import Accordion from '../../components/accordion';
import RenditionExampleContainer from '../../components/rendition-example-container';
import RenditionExample from '../../components/rendition-example';
import exampleRenditionHD from '../../video/rendition-hd-example.mp4';
import exampleRenditionImageDownsized from '../../images/rendition-downsized-example.gif';
import exampleRenditionFixedWidth from '../../images/rendition-200w-example.gif';
import exampleRenditionFixedHeight from '../../images/rendition-200h-example.gif';
import { Feature } from '../../components/optional-settings/features';
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h2>{`Rendition Guide`}</h2>
    <RenditionExampleContainer contentHeight="512px" mdxType="RenditionExampleContainer">
    <RenditionExample title="HD - MP4" filesize="4.6 MB" video={exampleRenditionHD} mdxType="RenditionExample" />
    </RenditionExampleContainer>
    <RenditionExampleContainer contentHeight="245px" mdxType="RenditionExampleContainer">
    <RenditionExample title="Downsized" filesize="86 KB" image={exampleRenditionImageDownsized} mdxType="RenditionExample" />
    <RenditionExample title="Fixed Width" filesize="2.1 MB" image={exampleRenditionFixedWidth} mdxType="RenditionExample" />
    <RenditionExample title="Fixed Height" filesize="4.9 MB" image={exampleRenditionFixedHeight} mdxType="RenditionExample" />
    </RenditionExampleContainer>
    <p style={{
      "marginTop": "40px"
    }}>
    GIPHY provides a wide range of GIF Object renditions so that developers customize their UIs and provide the best
    experiences to their users. In general we recommend:
    </p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`Using MP4 and WEBP renditions where supported`}</strong>{`. This will maximize quality and reduce load times.`}</li>
      <li parentName="ul"><strong parentName="li">{`Using GIF or WEBP when you want GIPHY Stickers`}</strong>{`. Unfortunately, MP4 does not support transparency; thankfully, sticker file sizes are usually smaller.`}</li>
      <li parentName="ul"><strong parentName="li">{`Use the smaller fixed_height or fixed_width renditions on your preview grid`}</strong>{`. These smaller sizes are more performant when you want to load many GIF objects at once.`}</li>
      <li parentName="ul"><strong parentName="li">{`Use the largest possible rendition for the share or send`}</strong>{`. Once the user has selected a GIF, give them the best quality possible!`}</li>
    </ul>
    <p>{`Of course, these configurations are just some of our best practices – GIPHY’s renditions can be configured in all sorts of different ways. Have a novel use case and need some advice? Feel free to reach out: `}<a parentName="p" {...{
        "href": "mailto:api@giphy.com"
      }}>{`api@giphy.com`}</a></p>
    <Feature title="PLEASE NOTE" descriptions={['The only formats that support transparency are GIF and WEBP, so if you are building an experience that includes stickers, make sure to avoid MP4 where you would like to see transparent content. Likewise, different browsers and operating systems support different media types, and you should tailor your media selections to the environment in which you plan to share.', 'When making requests that return GIF objects, the “Accept” HTTP header can be used to toggle the content type you will receive in return. For example, by making a request to one of our gif URLs with an “Accept” header that begins with “text/html” we assume the request is being made from a browser and render HTML to display the image with extra data. If the “Accept” header begins with any other pattern, then the binary media file for the GIF file type is returned.', 'You should not rely on size value in the API response being exactly correct every time. This is because we may choose to replace some pre-built renditions with dynamically generated renditions, and we will therefore not have an exact size value in advance.']} mdxType="Feature" />
    <h3>{`Rendition Best Practices by Use Case`}</h3>
    <p><strong parentName="p">{`GIF Grid Picker in a Mobile Application`}</strong>{`
`}<strong parentName="p">{`Grid`}</strong>{`: fixed_height or fixed width MP4 or WEBP.
`}<strong parentName="p">{`Send`}</strong>{`: downsized_large, downsized, or downsized_medium MP4 or WEBP.`}</p>
    <Accordion mdxType="Accordion">
      <p>{`Many chat and messaging apps use a grid for searching and selecting media. In mobile applications, it is common to implement this grid at either a fixed height or fixed width. To avoid cutting images off, and to present the highest resolution image to your users at the lightest file size, we would recommend using the fixed_height or fixed_width renditions, both of which are scaled to 200px on the appropriate axis in all formats. Use our MP4 or WEBP format for the grid, to speed loading times. You may also choose to load the fixed_height_still or fixed_width_still initially to provide a more seamless scrolling experience.`}</p>
      <p>{`When the user selects a gif object, developers should call a higher quality rendition to send through to the application. We recommend the downsized_large rendition for this implementation. If you are concerned that this rendition is too large to load quickly, downsized_medium and then downsized are also good options.`}</p>
    </Accordion>
    <p><strong parentName="p">{`GIF Scroll Picker in a Mobile Application`}</strong>{`
`}<strong parentName="p">{`Scroll`}</strong>{`: fixed_width_small or fixed_height_small.
`}<strong parentName="p">{`Send`}</strong>{`: downsized_large, downsized, or downsized_medium.`}</p>
    <Accordion mdxType="Accordion">
      <p>{`Another common way to implement GIPHY in a Chat application is to implement the `}<a href="/docs/api/endpoint#trending">{`Trending Endpoint`}</a>{` as a scrolling selection above the keyboard. This calls for a smaller rendition to be used for the preview. We recommend the fixed_width_small or fixed_height_small rendition for this implementation. If speed and/or transfer size are a concern, then you can use the downsampled version; this version is a big compromise on quality as it may remove many frames from the GIF.`}</p>
      <p>{`When the user selects a gif object, developers should call a higher quality rendition to send through to the application. We recommend the downsized_large rendition for this implementation. Again, if you are concerned that this rendition is too large to load quickly, downsized_medium and then downsized are also good options.`}</p>
    </Accordion>
    <p><strong parentName="p">{`Desktop Application Search and Send`}</strong>{`
`}<strong parentName="p">{`Grid`}</strong>{`: fixed_height or fixed width MP4 or WEBP.
`}<strong parentName="p">{`Send`}</strong>{`: original WEBP or MP4.`}</p>
    <Accordion mdxType="Accordion">
      <p>{`GIPHY has many high resolution options for desktop applications. Often the best option is the original rendition. If you are looking to decrease load times, choose the original.webp for Chrome users and the original.mp4 for all other browsers. For displaying a large quantity of search results, we recommend our fixed_height or fixed_width renditions in both WEBP and MP4, depending on browser type.`}</p>
    </Accordion>
    <p><strong parentName="p">{`Sticker Layering`}</strong>{`
`}<strong parentName="p">{`Grid`}</strong>{`: fixed_height or fixed_width webp or GIF.
`}<strong parentName="p">{`Send`}</strong>{`: original webp or GIF.`}</p>
    <Accordion mdxType="Accordion">
      <p>{`One common method of creating a GIF Sticker search interface is to load GIF Stickers into a preview view (probably a UICollectionView on iOS), where users can select one of the GIFs in their search result. The selected GIF is then placed into the image canvas, where it can be manipulated in various ways. Use a higher resolution GIF in the image canvas, but not in the preview view.`}</p>
      <p>{`Depending on layout, device type, or other needs, a fixed_height or fixed_width rendition will work for your grid. As soon as the user selects an image to drag and drop, your app should begin fetching a higher resolution rendition. For this, we recommend the original in WEBP or GIF.`}</p>
    </Accordion>
    <div className="rendition-type">
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Rendition Type`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Title`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Attribute`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Available Filetypes`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Original`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`original`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Original file size and dimensions`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`GIF, MP4, WEBP`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`original_still`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`A still version of the first frame of the original for previews and pre-loading.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`GIF`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`looping`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`A version of the original set to loop for exactly 15 seconds.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`MP4`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`HD`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`hd`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`A high definition video that is available if the source media meets HD criteria. This video is constrained between 720 and 1920 pixels on a side.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`MP4`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`4k`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`An ultra high definition video that is available if the source media meets 4K UHD criteria. This video is constrained between 2160 and 3840 pixels on a side.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`MP4`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Fixed Height`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`fixed_height`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Height set to 200px, variable width, to fit in preview grids and other smaller applications.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`GIF, MP4, WEBP`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`fixed_height_still`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`A still version of the first frame of the fixed_height rendition for previews and pre-loading.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`GIF`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`fixed_height_downsampled`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Downsampled to six frames for faster-loading unlimited scroll preview grids. Not for user selection.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`GIF, WEBP`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`fixed_height_small`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Height set to 100px, variable width. Good for smaller screen sizes with preview grids.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`GIF, MP4, WEBP`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`fixed_height_small_still`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`A still version of the first frame of the fixed_height_small rendition for previews and pre-loading.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`GIF`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Fixed Width`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`fixed_width`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Width set to 200px, variable height, to fit in preview grids and other smaller applications.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`GIF, MP4, WEBP`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`fixed_width_still`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`A still version of the first frame of the fixed_width rendition for previews and pre-loading.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`GIF`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`fixed_width_downsampled`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Downsampled to six frames for faster-loading unlimited scroll preview grids. Not for user selection.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`GIF, WEBP`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`fixed_width_small`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Width set to 100px, variable height. Good for smaller screen sizes with preview grids.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`GIF, MP4, WEBP`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`fixed_width_small_still`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`A still version of the first frame of the fixed_width_small rendition for previews and pre-loading.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`GIF`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Downsized`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`downsized`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Resized and downsampled to meet 2MB limit.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`GIF`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`downsized_still`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`A still version of the first frame of the downsized rendition for previews and pre-loading.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`GIF`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`downsized_large`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Resized and downsampled to meet 8MB limit.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`GIF`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`downsized_medium`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Resized and downsampled to meet 5MB limit.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`GIF`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`downsized_small`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Resized and downsampled to meet 200KB limit.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`MP4`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}>{`Preview`}</td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`preview`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Resized and trimmed to meet 50kB limit. Not recommended for most use cases.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`MP4`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}></td>
            <td parentName="tr" {...{
              "align": null
            }}><inlineCode parentName="td">{`preview_gif`}</inlineCode></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Resized and trimmed to meet 50kB limit. Not recommended for most use cases.`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`GIF`}</td>
          </tr>
        </tbody>
      </table>
    </div>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      